import React, { Suspense, useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { LayoutContainer, Layout, layoutConfigs } from '@bingads-webui/ad-library-layout-container';
import { Header, PageMenu, PageFooter, initWcpConsent } from '@bingads-webui/ad-library-layout-elements';
import { getInstrumentation, getInstrumentationService, InstrumentationServiceContext } from '@bingads-webui/ad-library-instrumentation';
import { FetchProvider } from '@bingads-webui/ad-library-fetch-layer';
import { PerformanceMarkerProvider } from '@bingads-webui-campaign-react/performance-marker-provider';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { staticI18nMock } from '@bingads-webui-test/i18n-mock'; // modelI18nMock
import { FluentProvider, webLightTheme, Spinner } from '@fluentui/react-components';

import ErrorBoundary from './error';

const FallbackSpinner = () => (
  <div style={{
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Spinner size="extra-large" />
  </div>
);

const Content = () => {
  const location = useLocation();
  return (
    <ErrorBoundary routeKey={location.key}>
      <Suspense fallback={<FallbackSpinner />}>
        <main id="main" style={{ flex: '1 0 auto', display: 'flex', flexDirection: 'column' }}>
          <Outlet />
        </main>
        <PageFooter />
      </Suspense>
    </ErrorBoundary>
  );
};

function App() {
  const serverContext = useMemo(() => ({
    DynamicPermissions: {
      EnableLoggingForUnhandledPromiseRejections: true,
    },
    PageTrackingId: 1,
    CurrentCustomer: {},
    CurrentAccount: {},
    CurrentUser: {},
  }), []);

  const instrumentation = useMemo(() => getInstrumentation({
    serverContext,
  }), [serverContext]);

  const instrumentationService = useMemo(() => getInstrumentationService({
    instrumentation,
    serverContext,
  }), [instrumentation, serverContext]);

  useEffect(() => {
    initWcpConsent({ logger: instrumentationService });
  }, [instrumentationService]);

  return (
    <FluentProvider theme={webLightTheme}>
      <I18nContext.Provider value={staticI18nMock} >
        <InstrumentationServiceContext.Provider value={instrumentationService}>
          <PerformanceMarkerProvider
            name="perf_marker_ad_library"
            instrumentation={instrumentation}
            immediateDone
          >
            <FetchProvider instrumentation={instrumentation}>
              <LayoutContainer
                pageConfig={layoutConfigs.default}
              >
                <div className="nav-skip" role="navigation">
                  <a href="#main">{_TL_('Skip to main content')}</a>
                </div>
                <Layout item="header" component={Header} />
                <Layout item="nav" component={PageMenu} />
                <Layout item="content" component={Content} />
              </LayoutContainer>
            </FetchProvider>
          </PerformanceMarkerProvider>
        </InstrumentationServiceContext.Provider>
      </I18nContext.Provider>
    </FluentProvider>
  );
}

export default App;
